import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import {
  Package,
  Plane,
  Boxes,
  Timer,
  PoundSterling,
  Container,
  Truck,
  Ship,
  Wallet,
  Loader2,
} from "lucide-react";
import { useAnalytics } from "@/lib/useAnalytics";
import { KpiCard } from "@/components/KpiCard";
import { ChartCard, SectionTitle } from "@/components/ChartCard";
import { SummaryPanel } from "@/components/SummaryPanel";
import { fmtInt, fmtGBP, fmtGBP2, fmtPct, fmtNum, monthLabel, CHART, tooltipStyle } from "@/lib/format";

const MODE_COLORS = [CHART.sea, CHART.air, CHART.cfs];

const t1 = (v, g = 90, w = 80) => (v >= g ? "good" : v >= w ? "warn" : "bad");
const t2 = (v, g = 85, w = 70) => (v >= g ? "good" : v >= w ? "warn" : "bad");

const Overview = () => {
  const { data, isLoading } = useAnalytics("overview", "/analytics/overview");

  if (isLoading || !data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const k = data.kpis;
  const s = data.supplier;
  const o = data.operational;
  const v = data.volume;
  const f = data.finance;
  const trend = data.trend.map((x) => ({ ...x, label: monthLabel(x.month) }));
  const modeSub = data.by_mode.map((m) => `${m.mode} ${fmtInt(m.count)}`).join(" · ");

  const supplierStats = [
    { label: "PO Handover", value: fmtPct(s.po_handover), tone: t1(s.po_handover), info: "% of purchase orders handed over by the supplier on or before the agreed cut-off date." },
    { label: "On-Time Booking", value: fmtPct(s.on_time_booking), tone: t1(s.on_time_booking, 88, 80), info: "% of shipments booked with the carrier within the agreed booking window." },
    { label: "Doc Handover", value: fmtPct(s.doc_handover), tone: t1(s.doc_handover), info: "% of shipments where shipping documents were submitted on time." },
    { label: "Doc Accuracy", value: fmtPct(s.doc_accuracy), tone: t1(s.doc_accuracy), info: "% of shipping documents received complete and error-free." },
    { label: "Late Orders", value: fmtInt(s.late_orders), tone: "neutral", info: "Number of orders delivered after the customer's Required Delivery Date (RDD)." },
  ];
  const opStats = [
    { label: "On-Time Departure", value: fmtPct(o.on_time_departure), tone: t2(o.on_time_departure), info: "% of shipments that departed on schedule against the required delivery date." },
    { label: "On-Time Arrival", value: fmtPct(o.on_time_arrival), tone: t2(o.on_time_arrival), info: "% of shipments that arrived at the destination port/airport on or before ETA." },
    { label: "On-Time Customs", value: fmtPct(o.on_time_customs), tone: t2(o.on_time_customs), info: "% of shipments cleared through customs within the target time." },
    { label: "On-Time Delivery", value: fmtPct(o.on_time_delivery), tone: t2(o.on_time_delivery), info: "% of shipments delivered on or before the customer's Required Delivery Date." },
    { label: "Delays", value: fmtInt(o.delays), tone: o.delays === 0 ? "good" : "neutral", info: "Shipments flagged with a transit exception (missed arrival or delivery), each tagged with a reason code." },
  ];
  const volStats = [
    { label: "Sea TEUs", value: fmtNum(v.sea_teu) },
    { label: "Air Freight kg", value: fmtInt(v.air_kg) },
    { label: "Road CBM", value: fmtNum(v.road_cbm) },
    { label: "LCL CBM", value: fmtNum(v.lcl_cbm) },
    { label: "CFS / Consol CBM", value: fmtNum(v.cfs_cbm) },
  ];
  const finStats = [
    { label: "Freight Cost", value: fmtGBP(f.freight) },
    { label: "Duty Cost", value: fmtGBP(f.duty) },
    { label: "Exceptional", value: fmtGBP(f.exceptional), tone: "warn" },
    { label: "Cost / Unit", value: fmtGBP2(f.cost_per_unit) },
  ];

  return (
    <div>
      <SectionTitle
        title="Executive Overview"
        description="Brand Machine Group · freight forwarding performance across supplier, operations, volume and cost"
      />

      {/* Hero KPI strip */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        <KpiCard testId="kpi-shipments" className="rise" label="Total Shipments" value={fmtInt(k.total_shipments)} sub={modeSub} icon={Package} />
        <KpiCard testId="kpi-otd" className="rise delay-1" label="On-Time Delivery" value={fmtPct(k.on_time_delivery)} icon={Timer} tone={t2(k.on_time_delivery)} />
        <KpiCard testId="kpi-teu" className="rise delay-2" label="Sea TEUs" value={fmtNum(k.total_teu)} icon={Container} />
        <KpiCard testId="kpi-airkg" className="rise delay-2" label="Air Freight (kg)" value={fmtInt(k.total_air_kg)} icon={Plane} />
        <KpiCard testId="kpi-cost" className="rise delay-3" label="Total Cost" value={fmtGBP(k.total_cost)} sub="Freight + duty + exceptional · all modes" icon={PoundSterling} />
      </div>

      {/* Charts */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <ChartCard
          testId="chart-shipment-trend"
          title="Shipment Volume & On-Time Delivery"
          subtitle="Monthly on-time delivery %"
          className="lg:col-span-2"
        >
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trend} margin={{ left: -18, right: 8, top: 8 }}>
              <defs>
                <linearGradient id="otd" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART.sea} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={CHART.sea} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
              <YAxis tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="on_time_delivery" name="On-Time Delivery %" stroke={CHART.sea} strokeWidth={2} fill="url(#otd)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testId="chart-mode-split" title="Modal Split" subtitle="Shipments by transport mode">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.by_mode} dataKey="count" nameKey="mode" innerRadius={58} outerRadius={92} paddingAngle={2}>
                {data.by_mode.map((_, i) => (
                  <Cell key={i} fill={MODE_COLORS[i % MODE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Section KPI summary panels */}
      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <SummaryPanel testId="panel-supplier" title="Supplier Management" icon={Truck} to="/supplier" stats={supplierStats} />
        <SummaryPanel testId="panel-operational" title="Operational Management" icon={Ship} to="/operational" stats={opStats} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <SummaryPanel testId="panel-volume" title="Volume" icon={Boxes} to="/volume" stats={volStats} />
        <SummaryPanel testId="panel-finance" title="Finance" icon={Wallet} to="/finance" stats={finStats} cols="grid-cols-2 sm:grid-cols-4" />
      </div>

      {/* Bottom charts */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard testId="chart-origins" title="Shipments by Origin" subtitle="Sourcing footprint">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.top_origins} layout="vertical" margin={{ left: 20, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
              <YAxis type="category" dataKey="origin" width={90} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--secondary))" }} />
              <Bar dataKey="shipments" fill={CHART.sea} radius={[0, 3, 3, 0]} barSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testId="chart-cost-trend" title="Monthly Cost" subtitle="Total logistics spend by month">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={trend} margin={{ left: -6, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
              <YAxis tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" tickFormatter={(x) => `£${(x / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(x) => fmtGBP(x)} cursor={{ fill: "hsl(var(--secondary))" }} />
              <Bar dataKey="cost" name="Total cost" fill={CHART.cfs} radius={[3, 3, 0, 0]} barSize={26} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

export default Overview;
